import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { noop } from 'rxjs';
import { withDefaultFalse, withDefaultString } from '../base.transforms';

let _nextId = 0;

/**
 * Generates unique IDs for input elements to ensure proper form field associations.
 *
 * @remarks
 * This utility function creates sequential unique identifiers for input elements using
 * the format `bb_input_${n}` where n is an incrementing counter. It's primarily used
 * internally by input components to generate default IDs when none are provided,
 * ensuring proper label-to-input associations and accessibility compliance.
 *
 * @returns A unique string identifier in the format `bb_input_${n}`.
 *
 * @example
 *
 * ```typescript
 * import { getInputNextId } from '@backbase/ui-ang/base-classes';
 *
 * @Component({
 *   selector: 'bb-currency-input',
 * })
 * export class CurrencyInputComponent {
 *   private _integerId = getInputNextId();
 *   private _decimalId = getInputNextId();
 * }
 * ```
 */
export function getInputNextId() {
  return `bb_input_${_nextId++}`;
}

/**
 * Default size value for input components.
 *
 * @example
 *
 * ```typescript
 * import { defaultSize } from '@backbase/ui-ang/base-classes';
 *
 * const SIZE_MEDIUM = defaultSize;
 * const SIZE_SMALL = SIZE_MEDIUM / 2;
 * ```
 */
export const defaultSize = 20;

/**
 * Widget roles specified in ARIA 1.1
 *
 * @example
 *
 * ```typescript
 * if (typeof value === 'string' && VALID_WIDGET_ROLES.indexOf(value) === -1) {
 *   // Prevent setting invalid roles
 *   value = undefined;
 * }
 * ```
 */
const VALID_WIDGET_ROLES = [
  'button',
  'checkbox',
  'combobox',
  'grid',
  'gridcell',
  'link',
  'listbox',
  'menu',
  'menubar',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'progressbar',
  'radio',
  'radiogroup',
  'scrollbar',
  'searchbox',
  'separator',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'tablist',
  'tabpanel',
  'textbox',
  'tree',
  'treegrid',
  'treeitem',
];

/**
 * Properties interface for input base components.
 *
 * @remarks
 * Defines the common properties that all input-based components should support,
 * including accessibility attributes and basic form field properties.
 *
 * @example
 *
 * ```typescript
 * export class InputBaseComponent implements InputBaseComponentProps, ControlValueAccessor {
 *   // Implementation provides all properties from InputBaseComponentProps
 * }
 * ```
 */
export interface InputBaseComponentProps {
  /**
   * The id for the input. Defaults to unique string.
   * Used to map the label to the input.
   */
  id: string;

  /**
   * The label for the input. Defaults to an empty string.
   */
  label: string;

  /**
   * Customize the ARIA role for the HTML input/select/textarea element inside this component.
   *
   * This can be used to improve accessibility for components, for example by configuring `[role]="'combobox'"`
   * for a component that provides an autocomplete list.
   *
   * Values that are valid for the native HTML form elements are allowed.
   */
  role: string | undefined;

  /**
   * Configure the minimum width to fit the specified number of characters that should fit for HTML `<input>`
   */
  size: number | string;

  /**
   * Class names that must be included on the `<input>` form control
   */
  inputClassName?: string;

  /**
   * It indicates whether inputting text could trigger display of one or more
   * predictions of the user's intended value for a combobox, searchbox, or textbox.
   */
  ariaAutocomplete?: string;

  /**
   * It identifies the currently active element when focus is on a
   * composite widget, combobox, textbox, group, or application.
   */
  ariaActivedescendant?: string;

  /**
   * When an interactive or input control has an impact on another element in a document or application,
   * the aria-control indicates which element or elements the user interface widget controls.
   */
  ariaControls?: string;

  /**
   * Set aria-describedby  with an element id that contains a detailed decription of the widget.
   * It is used to establish a relationship between widgets or groups and the text that describes them.
   */
  ariaDescribedby?: string;

  /**
   * The aria-expanded attribute is set on an element to indicate if a control is expanded or collapsed,
   * and whether or not its child elements are displayed or hidden.
   */
  ariaExpanded?: string;

  /**
   * The aria-invalid state indicates the entered value is not in a format expected by the application.
   */
  ariaInvalid?: string;

  /**
   * Accessible label when control does not need to render label tag.
   */
  ariaLabel?: string;

  /**
   * The aria-labelledby property enables authors to reference other elements on the page to define an
   * accessible name. This is useful when using elements that don't have native support for associating
   * elements to provide an accessible name.
   */
  ariaLabelledby?: string;

  /**
   * The aria-owns attribute identifies an element (or elements) in order to define a visual, functional, or
   * contextual relationship between a parent and its child elements when the DOM hierarchy cannot be used
   * to represent the relationship.
   */
  ariaOwns?: string;

  /**
   * Whether the input is required. Defaults to false.
   */
  required: boolean;

  /**
   * Whether the input is read only. Defaults to false.
   */
  readonly: boolean;

  /**
   * Whether the text input should be auto-focused when shown.
   */
  autofocus: boolean;

  /**
   * Whether the component is mutable or clickable. Defaults to false.
   */
  disabled: boolean;
}

/**
 * Base component providing common functionality for form input components.
 *
 * @remarks
 * {@link InputBaseComponent} serves as the foundation for all input components in the design system.
 * It implements {@link ControlValueAccessor} to integrate with Angular's reactive forms and template-driven
 * forms. The component provides standard input properties like label, id, validation states, and
 * accessibility attributes. It also handles common input behaviors such as focus management,
 * disabled states, and form validation integration.
 *
 * All input components should extend this base class to ensure consistent behavior and API across
 * the design system. The component includes built-in accessibility features and follows ARIA guidelines
 * for form controls.
 *
 * @example
 *
 * ```typescript
 * import { InputBaseComponent } from '@backbase/ui-ang/base-classes';
 *
 * @Component({
 *   selector: 'bb-input-text',
 *   templateUrl: './input-text.component.html',
 * })
 * export class InputTextComponent extends InputBaseComponent implements InputTextComponentProps, AfterViewInit {
 *   @Input({ transform: withDefaultString }) placeholder = '';
 *   @Input({ transform: withDefaultString }) mask = '';
 * }
 * ```
 */
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InputBaseComponent implements InputBaseComponentProps, ControlValueAccessor {
  private _id = getInputNextId();
  private _labelClasses: string | undefined;

  /**
   * Callback function that is called when the control becomes touched.
   *
   * @remarks
   * This function is set by Angular's reactive forms system through the {@link ControlValueAccessor}
   * interface. It gets invoked automatically when the input loses focus or when manually triggered
   * to mark the control as touched for validation purposes.
   *
   * @example
   *
   * ```typescript
   * // Mark control as touched
   * this.onTouched();
   * ```
   */
  onTouched: () => void = noop;
  /**
   * Sets the unique identifier for the input element.
   *
   * @remarks
   * The id property is used to establish a relationship between the label and the input element
   * for accessibility purposes. It's also used as a base for generating related element IDs.
   *
   * @param value - The unique identifier string
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.id]="id"
   *   class="bb-input-checkbox__input"
   * />
   * ```
   */
  @Input()
  set id(value: string) {
    this._id = value;
  }

  /**
   * Gets the unique identifier for the input element.
   *
   * @remarks
   * This getter returns the current ID value, which is used both for the input element itself
   * and as a base for generating IDs for related elements like labels and validation messages.
   *
   * @returns The unique identifier string
   *
   * @example
   *
   * ```html
   * <!-- Accessing the ID in template -->
   * <label [for]="id">{{label}}</label>
   * <input [id]="id" [value]="value" />
   * ```
   */
  get id(): string {
    return this._id;
  }

  /**
   * The label text for the input element.
   *
   * @remarks
   * This property provides the accessible label text that describes the input field. It's typically
   * displayed as visible text and associated with the input element for screen readers and other
   * assistive technologies.
   *
   * @example
   *
   * ```html
   * <bb-input-component
   *   [label]="props.label"
   *   [required]="props.required"
   * />
   * ```
   */
  @Input({ transform: withDefaultString }) label = '';

  private _role: string | undefined;

  /**
   * Gets the ARIA role for the input element.
   *
   * @remarks
   * This getter returns the current ARIA role value which customizes the semantic meaning
   * of the input element for assistive technologies. Only valid ARIA widget roles are allowed
   * as defined in the {@link VALID_WIDGET_ROLES} array.
   *
   * @returns The ARIA role string or undefined if no role is set
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   [role]="props.role"
   *   [aria-invalid]="formControl.invalid.toString()"
   * />
   * ```
   */
  @Input()
  get role(): string | undefined {
    return this._role;
  }

  /**
   * Sets the ARIA role for the input element.
   *
   * @remarks
   * This setter validates and assigns the ARIA role value. Invalid roles are automatically
   * rejected and the value is set to undefined. Only roles from the {@link VALID_WIDGET_ROLES}
   * array are accepted to ensure accessibility compliance.
   *
   * @param value - The ARIA role string to set, or undefined to clear the role
   *
   * @example
   *
   * ```html
   * <bb-account-selector-ui
   *   [role]="'combobox'"
   * />
   * ```
   */
  set role(value: string | undefined) {
    if (typeof value === 'string' && VALID_WIDGET_ROLES.indexOf(value) === -1) {
      // Prevent setting invalid roles
      // TODO: Log a warning about the invalid role
      value = undefined;
    }

    this._role = value;
  }

  /**
   * @internal
   */
  protected _size: number = defaultSize;

  /**
   * Gets the size configuration for the input element.
   *
   * @remarks
   * This getter returns the current size value which controls the minimum width to fit
   * the specified number of characters for HTML input elements. The value can be either
   * a number or a string representation.
   *
   * @returns The size value as number or string
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   [size]="props.size"
   * />
   * ```
   */
  @Input() get size(): number | string {
    return this._size;
  }

  /**
   * Sets the size configuration for the input element.
   *
   * @remarks
   * This setter processes the size value, supporting both numeric values and keyword strings.
   * It automatically converts size keywords to integer values via [getSizeByKeyword]{@link #getSizeByKeyword} method
   * and validates that the final value is a positive integer.
   *
   * @param arg - The size value as number, string, or size keyword
   *
   * @example
   *
   * ```html
   * <bb-input-number-ui
   *   [size]="props.size"
   * />
   * ```
   */
  set size(arg: number | string) {
    const size = this.getSizeByKeyword(arg);

    // Optionally convert a `size` keyword to an integer value.
    // This can be supported by subclasses of `InputBaseComponent`
    arg = typeof size !== 'undefined' ? size : arg;

    const int = parseInt(String(arg), 10);

    if (int >= 1 && !isNaN(int)) {
      this._size = int;
    }
  }

  /**
   * Additional CSS class names to apply to the input form control element.
   *
   * @remarks
   * This property allows custom styling by adding CSS classes directly to the input element.
   * The classes are applied using Angular's ngClass directive and can be used to override
   * or extend the default styling.
   *
   * @example
   *
   * ```html
   * <input
   *   [bbFocus]="autofocus"
   *   [ngClass]="inputClassName"
   *   [attr.data-role]="'input'"
   * />
   * ```
   */
  @Input() inputClassName?: string;
  /**
   * Indicates whether inputting text could trigger display of predictions for autocomplete.
   *
   * @remarks
   * This ARIA attribute indicates whether inputting text could trigger display of one or more
   * predictions of the user's intended value for a combobox, searchbox, or textbox. Valid
   * values include 'none', 'inline', 'list', or 'both'.
   *
   * @example
   *
   * ```html
   * <input
   *   #inputText
   *   [attr.aria-autocomplete]="ariaAutocomplete"
   *   [attr.aria-controls]="ariaControls"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-autocomplete') ariaAutocomplete?: string;

  /**
   * Identifies the currently active element when focus is on a composite widget.
   *
   * @remarks
   * This ARIA attribute identifies the currently active element when focus is on a
   * composite widget, combobox, textbox, group, or application. It references the ID
   * of the descendant element that is currently active or selected.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-activedescendant]="ariaActivedescendant"
   *   [attr.aria-controls]="ariaControls"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-activedescendant') ariaActivedescendant?: string;

  /**
   * Identifies elements that the input controls or affects.
   *
   * @remarks
   * This ARIA attribute indicates which element or elements the user interface widget controls.
   * It establishes a relationship between the input and the elements it affects, typically
   * referenced by their IDs.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-controls]="ariaControls"
   *   [attr.aria-expanded]="ariaExpanded"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-controls') ariaControls?: string;

  /**
   * References elements that describe the input field.
   *
   * @remarks
   * This ARIA attribute establishes a relationship between the input and elements that provide
   * additional descriptive information. It typically references help text, error messages,
   * or other descriptive content by their IDs.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-describedby]="ariaDescribedby"
   *   [attr.aria-invalid]="ariaInvalid"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-describedby') ariaDescribedby?: string;

  /**
   * Indicates if the input controls an expandable element.
   *
   * @remarks
   * This ARIA attribute indicates whether a collapsible element controlled by the input
   * is currently expanded or collapsed. Valid values are 'true', 'false', or undefined.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-expanded]="ariaExpanded"
   *   [attr.aria-controls]="ariaControls"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-expanded') ariaExpanded?: string;

  /**
   * Indicates the entered value does not conform to the expected format.
   *
   * @remarks
   * This ARIA attribute indicates the validation state of the input. Valid values are
   * 'true' (invalid), 'false' (valid), 'grammar' (grammar error), 'spelling' (spelling error),
   * or undefined.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-invalid]="ariaInvalid"
   *   [attr.aria-describedby]="ariaDescribedby"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-invalid') ariaInvalid?: string;

  /**
   * Provides an accessible label when visible label text is not available.
   *
   * @remarks
   * This ARIA attribute provides an accessible name for the input when a visible label
   * is not present or when the visible label text is insufficient. This attribute
   * takes precedence over aria-labelledby.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-label]="ariaLabel"
   *   [placeholder]="placeholder"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-label') ariaLabel?: string;

  /**
   * References elements that provide an accessible name for the input.
   *
   * @remarks
   * This ARIA attribute identifies one or more elements that label the input by referencing
   * their IDs. Unlike aria-label, this allows the label to be visible on screen and
   * enables more complex labeling scenarios.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-labelledby]="ariaLabelledby"
   *   [attr.aria-describedby]="ariaDescribedby"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-labelledby') ariaLabelledby?: string;

  /**
   * Identifies elements that are owned by the input.
   *
   * @remarks
   * This ARIA attribute establishes a parent-child relationship when the DOM hierarchy
   * cannot represent the relationship. It's used to identify elements that are logically
   * owned by the input but may be located elsewhere in the DOM.
   *
   * @example
   *
   * ```html
   * <input
   *   [attr.aria-owns]="ariaOwns"
   *   [attr.aria-expanded]="ariaExpanded"
   * />
   * ```
   */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('aria-owns') ariaOwns?: string;

  // `aria-readonly` is not needed, use `readonly` instead
  // `aria-required` is not needed, use `required` instead

  /**
   * Indicates whether the input field is required for form submission.
   *
   * @remarks
   * This boolean property marks the input as required, triggering native HTML5 validation
   * and accessibility features. Screen readers will announce required fields to users.
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   [required]="props.required"
   *   [label]="props.label"
   * />
   * ```
   */
  @Input({ transform: withDefaultFalse }) required = false;
  /**
   * Indicates whether the input field is read-only.
   *
   * @remarks
   * This boolean property makes the input field read-only, preventing user modifications
   * while keeping the value visible and accessible. The field remains focusable and
   * the value is included in form submission.
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   [readonly]="isReadonly"
   *   [value]="displayValue"
   * />
   * ```
   */
  @Input({ transform: withDefaultFalse }) readonly = false;
  /**
   * An event emitter for on blur actions.
   *
   * @remarks
   * This output event is emitted when the input loses focus. It can be used to handle
   * blur events at the component level for validation, formatting, or other interactions.
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   (blur)="onInputBlur($event)"
   *   [value]="inputValue"
   * />
   * ```
   */
  @Output() blur = new EventEmitter<FocusEvent | void>();
  /**
   * An event emitter for on focus actions.
   *
   * @remarks
   * This output event is emitted when the input gains focus. It can be used to handle
   * focus events at the component level for UI interactions, highlighting, or other
   * focus-related behaviors.
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   (focus)="onInputFocus($event)"
   *   [value]="inputValue"
   * />
   * ```
   */
  @Output() focus = new EventEmitter<FocusEvent | void>();
  /**
   * Indicates whether the input should automatically receive focus when rendered.
   *
   * @remarks
   * This boolean property controls whether the input element automatically gains focus
   * when the component is initialized. Only one element per page should have autofocus
   * to ensure proper accessibility and user experience.
   *
   * @example
   *
   * ```html
   * <input
   *   [bbFocus]="autofocus"
   *   [ngClass]="inputClassName"
   * />
   * ```
   */
  @Input({ transform: withDefaultFalse }) autofocus = false;

  /**
   * @internal
   */
  protected readonly _valueChange = new EventEmitter<any>();

  /**
   * Indicates whether the input component is disabled.
   *
   * @remarks
   * This boolean property controls whether the input is disabled, preventing user interaction.
   * Disabled inputs are not focusable, cannot be edited, and their values are not included
   * in form submission. The visual styling also changes to indicate the disabled state.
   *
   * @example
   *
   * ```html
   * <bb-input-text-ui
   *   [disabled]="formControl.disabled"
   *   [value]="formControl.value"
   * />
   * ```
   */
  @Input({ transform: withDefaultFalse }) disabled = false;

  /**
   * The current value of the input component.
   *
   * @remarks
   * This property holds the current value state of the input. It can be an Object, string,
   * boolean, or null depending on the specific input type. The value is managed through
   * the ControlValueAccessor interface for Angular forms integration.
   *
   * @example
   *
   * ```typescript
   * // Setting value programmatically
   * this.value = 'new input value';
   * this._valueChange.emit(this.value);
   * ```
   */
  value: Object | string | boolean | null = '';

  constructor(protected readonly cd: ChangeDetectorRef) {}

  /**
   * Handles the blur event when the input loses focus.
   *
   * @remarks
   * This method is called when the input element loses focus. It emits the blur event
   * and calls the onTouched callback to mark the control as touched for form validation
   * purposes.
   *
   * @param $event - Optional FocusEvent object containing blur event details
   *
   * @example
   *
   * ```html
   * <input
   *   (blur)="onBlur($event)"
   *   [value]="value"
   * />
   * ```
   */
  onBlur($event?: FocusEvent) {
    this.blur.emit($event);
    this.onTouched();
  }

  /**
   * Callback function for handling value changes.
   *
   * @remarks
   * This callback is set by Angular's reactive forms system through the ControlValueAccessor
   * interface. It's called whenever the component value changes to notify the parent form
   * control of the new value.
   *
   * @example
   *
   * ```typescript
   * // Called when value changes
   * this.onChange(newValue);
   * ```
   */
  onChange: (newValue: Object | string | undefined | null) => void = () => {};

  /**
   * Handles value changes and updates the component state.
   *
   * @remarks
   * This method is responsible for updating the component's internal value state and
   * notifying the parent form control of changes. It handles both undefined values
   * (using current value) and new values (updating state and emitting events).
   *
   * @param newValue - The new value to set, or undefined to use current value
   *
   * @example
   *
   * ```typescript
   * // Update with new value
   * this.onValueChange('new text value');
   *
   * // Use current value
   * this.onValueChange();
   * ```
   */
  onValueChange(newValue?: Object | string) {
    if (newValue === undefined) {
      this.onChange(this.value);
    } else {
      this.onChange(newValue);
      this.value = newValue;
      this._valueChange.emit(this.value);
    }
  }

  /**
   * Handles the focus event when the input gains focus.
   *
   * @remarks
   * This method is called when the input element gains focus. It emits the focus event
   * to notify parent components that the input has been focused.
   *
   * @param $event - Optional FocusEvent object containing focus event details
   *
   * @example
   *
   * ```html
   * <input
   *   (focus)="onFocus($event)"
   *   [value]="value"
   * />
   * ```
   */
  onFocus($event?: FocusEvent) {
    this.focus.emit($event);
  }

  /**
   * Writes a new value to the component from the form model.
   *
   * @remarks
   * This method is part of the ControlValueAccessor interface and is called by Angular's
   * reactive forms system to update the component's value when the form model changes.
   * It handles null values by converting them to empty strings.
   *
   * @param inputValue - The new value from the form model
   *
   * @example
   *
   * ```typescript
   * // Called by Angular forms
   * this.writeValue('new form value');
   * this.writeValue(null); // converts to empty string
   * ```
   */
  writeValue(inputValue: Object | string | null): void {
    this.value = inputValue === null ? '' : inputValue;
    this.cd.markForCheck();
  }

  /**
   * Registers a callback function for handling value changes.
   *
   * @remarks
   * This method is part of the ControlValueAccessor interface and is called by Angular's
   * reactive forms system to register the onChange callback. This callback is invoked
   * whenever the component's value changes.
   *
   * @param fn - The callback function to register for value changes
   *
   * @example
   *
   * ```typescript
   * // Registered by Angular forms
   * this.registerOnChange((value) => {
   *   console.log('Value changed:', value);
   * });
   * ```
   */
  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function for handling touched state changes.
   *
   * @remarks
   * This method is part of the ControlValueAccessor interface and is called by Angular's
   * reactive forms system to register the onTouched callback. This callback is invoked
   * whenever the component becomes touched (typically on blur events).
   *
   * @param fn - The callback function to register for touched state changes
   *
   * @example
   *
   * ```typescript
   * // Registered by Angular forms
   * this.registerOnTouched(() => {
   *   console.log('Component touched');
   * });
   * ```
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the component.
   *
   * @remarks
   * This method is part of the ControlValueAccessor interface and is called by Angular's
   * reactive forms system to enable or disable the component. It updates the disabled
   * property and triggers change detection.
   *
   * @param isDisabled - Whether the component should be disabled
   *
   * @example
   *
   * ```typescript
   * // Called by Angular forms
   * this.setDisabledState(true);  // Disable component
   * this.setDisabledState(false); // Enable component
   * ```
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  /**
   * Returns the `input` `size` value for a keyword, such as "xxl".
   * Subclasses can choose to implement a custom mapping, which will be used to configure `size`.
   *
   * @remarks
   * This method provides an extension point for subclasses to implement custom size
   * keyword mappings. The base implementation returns undefined, meaning no mapping
   * is provided by default.
   *
   * @param arg - The size keyword to map to a numeric value
   * @returns The numeric size value, or undefined if no mapping exists
   *
   * @example
   *
   * ```typescript
   * // Subclass implementation
   * protected getSizeByKeyword(arg: any): number | undefined {
   *   const sizeMap = { small: 10, medium: 20, large: 30 };
   *   return sizeMap[arg] || undefined;
   * }
   * ```
   *
   * @internal
   */
  protected getSizeByKeyword(arg: any): number | undefined {
    return undefined;
  }
}
